import useUpdateEffect from '@pretto/bricks/core/utility/useUpdateEffect'

import {
  Illustration,
  InputComponent,
} from '@pretto/website/src/templates/blocs/components/simulatorRow/SimulatorRow.types'
import { formatCloudFunctionRatesToAdvancedSimuRates } from '@pretto/website/src/templates/simulateurAvance/lib/formatCloudFunctionRatesToAdvancedSimuRates'
import { useTracking } from '@pretto/website/src/utilities'

import { useMemo, useRef, useState } from 'react'

import * as INPUTS from '../config/inputs'
import * as calc from '../lib/calc'
import { getError, getMatch } from '../lib/helpers'

import { useDate } from './useDate'
import { useSlider } from './useSlider'

const DEFAULT_NUMBER_MORTGAGORS = 1

export const useRenegotiation = ({ trackButtonClick }, marketRatesProfileLatest) => {
  const trackAction = useTracking()
  const miniSimulator = formatCloudFunctionRatesToAdvancedSimuRates(marketRatesProfileLatest)
  const [newPayment, setNewPayment] = useState(0)
  const [savings, setSavings] = useState(0)
  const [newRate, setNewRate] = useState(0)
  const [creditCost, setCreditCost] = useState(0)
  const [isReadyForCalculations, setIsReadyForCalculations] = useState(false)
  const [errorFlag, setErrorFlag] = useState('')

  const handleClickResultsButton = () => {
    trackButtonClick('results')
  }

  const {
    value: duration,
    debouncedValue: debouncedDuration,
    onChangeInput: onChangeDuration,
    props: durationProps,
    inputType: durationInputType,
  } = useSlider('initialDuration')

  const {
    value: payment,
    debouncedValue: debouncedPayment,
    props: paymentProps,
    inputType: paymentInputType,
  } = useSlider('payment')

  const {
    value: rate,
    debouncedValue: debouncedRate,
    props: rateProps,
    inputType: rateInputType,
  } = useSlider('initialRate')

  const {
    value: income,
    debouncedValue: debouncedIncome,
    props: incomeProps,
    inputType: incomeInputType,
  } = useSlider('totalIncome')

  const {
    value: date,
    debouncedValue: debouncedDate,
    props: dateProps,
    inputType: dateInputType,
  } = useDate('signatureDate')

  const lastChangedInputTypes = {
    date: dateInputType,
    duration: durationInputType,
    income: incomeInputType,
    payment: paymentInputType,
    rate: rateInputType,
  }

  const previousData = useRef({ date, duration, income, payment, rate })

  useUpdateEffect(() => {
    const { max, min } = INPUTS.duration
    if (debouncedDuration < min || !debouncedDuration) {
      onChangeDuration(min)
      return
    }
    if (debouncedDuration > max) {
      onChangeDuration(max)
    }
  }, [debouncedDuration])

  useUpdateEffect(() => {
    const changes = { date, duration, income, payment, rate }
    const valueType = Object.entries(changes).reduce((previous, [key, value]) => {
      if (value !== previousData.current[key]) {
        return key
      }
      return previous
    }, false)

    previousData.current = changes

    if (valueType) {
      const inputType = lastChangedInputTypes[valueType]
      trackAction(`Calculator ${inputType} ${valueType} changed`, {
        calculatorVersion: 'v2',
        [valueType]: changes[valueType],
      })
    }

    if (duration > INPUTS.duration.max || duration < INPUTS.duration.min || income < 0 || payment < 0 || rate < 0) {
      return
    }

    const isReady = [duration, payment, rate, income, date].every(Boolean)

    if (!isReadyForCalculations && isReady) {
      setIsReadyForCalculations(true)
    }

    if (isReady) {
      const { loanAmount, remainingDuration } = calc.renegotiationLoanAmount({
        duration,
        payment,
        rate,
        signDate: date,
      })

      const { rate: newRate } = getMatch({
        duration: remainingDuration,
        income,
        miniSimulator,
        numberMortgagors: DEFAULT_NUMBER_MORTGAGORS,
      })

      const newPayment = calc.payment({ loanAmount, rate: newRate, duration: remainingDuration })

      const creditCost = calc.creditCost({
        payment: newPayment,
        duration: remainingDuration,
        loanAmount,
      })

      const savings = calc.savings({ payment, newPayment, remainingDuration })

      const errorFlag = getError({ payment, income, numberMortgagors: DEFAULT_NUMBER_MORTGAGORS })

      setNewPayment(newPayment)
      setSavings(savings)
      setNewRate(newRate)
      setCreditCost(creditCost)
      setErrorFlag(errorFlag)
    }
  }, [debouncedDuration, debouncedPayment, debouncedRate, debouncedIncome, debouncedDate])

  const inputBlocks = [
    {
      illustration: Illustration.SATISFIED,
      inputs: [
        {
          props: durationProps,
          type: InputComponent.SLIDER,
        },
        {
          props: paymentProps,
          type: InputComponent.SLIDER,
        },
        {
          props: dateProps,
          type: InputComponent.DATE,
        },
        {
          props: rateProps,
          type: InputComponent.SLIDER,
        },
        {
          props: incomeProps,
          type: InputComponent.SLIDER,
        },
      ],
      title: 'Votre prêt en cours',
    },
  ]

  const isOverlayVisible =
    !isReadyForCalculations && [duration, payment, date, rate, income].some(value => ![null, undefined].includes(value))

  const resultProps = useMemo(
    () => ({
      creditCost,
      isEmpty: !isReadyForCalculations,
      isOverlayVisible,
      onClickButton: handleClickResultsButton,
      payment: newPayment,
      rate: newRate,
      savings,
    }),
    [savings, creditCost, newPayment, newRate, isReadyForCalculations, isOverlayVisible]
  )
  return { errorFlag, graphProps: null, inputBlocks, isGraphActive: false, resultProps }
}
